import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Settings,
  Building2,
  Users,
  Search,
  Trash2,
  Shield,
  User,
  Crown,
  AlertTriangle,
  Calendar,
  Pencil,
} from 'lucide-react';
import { RootState } from '../../store';
import {
  getWorkspaceByIdRequest,
  removeUserFromWorkspaceRequest,
  updateWorkspaceSettingsRequest,
  clearWorkspaceState,
} from '../../store/workspace/workspace.action';
import {
  selectGetWorkspaceByIdError,
  selectGetWorkspaceByIdLoading,
  selectWorkspaceData,
  selectRemoveUserLoading,
  selectRemoveUserError,
  selectRemoveUserSuccess,
  selectUpdateWorkspaceSettingsLoading,
  selectUpdateWorkspaceSettingsSuccess,
  selectUpdateWorkspaceSettingsError,
} from 'store/workspace';
import { IWorkspaceUser } from '../../types/workspace/workspace.types';
import { IUserRole } from '../../types/user/user.types';
import Modal from '../../components/ui/Modal';
import Switch from '../../components/ui/Switch';
import Button from '../../components/ui/Button';
import { selectCurrentUser } from 'store/user';
import { useToast } from '../../contexts/ToastContext';
import DisableDuplicateEmailModal from '../../components/common/DisableDuplicateEmailModal';
import UpdateWorkspaceNameModal from '../../components/common/UpdateWorkspaceNameModal';

const WorkspaceSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const currentUser = useSelector(selectCurrentUser);
  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<IWorkspaceUser | null>(null);
  const [
    isDisableDuplicateEmailModalOpen,
    setIsDisableDuplicateEmailModalOpen,
  ] = useState(false);
  const [isUpdateWorkspaceNameModalOpen, setIsUpdateWorkspaceNameModalOpen] =
    useState(false);
  // Redux state
  const workspace = useSelector(selectWorkspaceData);
  const loading = useSelector((state: RootState) =>
    selectGetWorkspaceByIdLoading(state)
  );
  const error = useSelector((state: RootState) =>
    selectGetWorkspaceByIdError(state)
  );
  const removeUserLoading = useSelector((state: RootState) =>
    selectRemoveUserLoading(state)
  );
  const removeUserError = useSelector((state: RootState) =>
    selectRemoveUserError(state)
  );
  const removeUserSuccess = useSelector((state: RootState) =>
    selectRemoveUserSuccess(state)
  );
  const updateSettingsLoading = useSelector((state: RootState) =>
    selectUpdateWorkspaceSettingsLoading(state)
  );
  const updateSettingsSuccess = useSelector((state: RootState) =>
    selectUpdateWorkspaceSettingsSuccess(state)
  );
  const updateSettingsError = useSelector((state: RootState) =>
    selectUpdateWorkspaceSettingsError(state)
  );

  // Fetch workspace data on component mount
  useEffect(() => {
    if (!workspace) {
      dispatch(getWorkspaceByIdRequest());
    }
  }, [dispatch, workspace]);

  // Handle remove user success
  useEffect(() => {
    if (removeUserSuccess) {
      setIsRemoveUserModalOpen(false);
      setUserToRemove(null);
    }
  }, [removeUserSuccess]);

  // Handle remove user error
  useEffect(() => {
    if (removeUserError) {
      // Error is handled by the saga, but we can add additional error handling here if needed
      console.error('Remove user error:', removeUserError);
    }
  }, [removeUserError]);

  // Handle workspace settings update success
  useEffect(() => {
    if (updateSettingsSuccess) {
      showToast({
        type: 'success',
        title: 'Settings Updated',
        message: updateSettingsSuccess,
      });
      dispatch(clearWorkspaceState());
    }
  }, [updateSettingsSuccess, showToast, dispatch]);

  // Handle workspace settings update error
  useEffect(() => {
    if (updateSettingsError) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: updateSettingsError,
      });
      dispatch(clearWorkspaceState());
    }
  }, [updateSettingsError, showToast, dispatch]);

  // Helper functions
  const getRoleIcon = (role: IUserRole) => {
    switch (role) {
      case IUserRole.OWNER:
        return <Crown className="w-4 h-4" />;
      case IUserRole.ADMIN:
        return <Shield className="w-4 h-4" />;
      case IUserRole.MEMBER:
        return <User className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getRoleBadgeColor = (role: IUserRole) => {
    switch (role) {
      case IUserRole.OWNER:
        return 'bg-primary/10 text-primary border-primary/30';
      case IUserRole.ADMIN:
        return 'bg-info-background text-info border-info/30';
      case IUserRole.MEMBER:
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getRoleDisplayName = (role: IUserRole) => {
    switch (role) {
      case IUserRole.OWNER:
        return 'Owner';
      case IUserRole.ADMIN:
        return 'Admin';
      case IUserRole.MEMBER:
        return 'Member';
      default:
        return 'Member';
    }
  };

  const getFilteredUsers = () => {
    if (!workspace?.users) return [];

    const users = workspace.users as IWorkspaceUser[];
    if (!searchTerm) return users;

    return users.filter(
      (user) =>
        user.first_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        getRoleDisplayName(user?.role)
          ?.toLowerCase()
          .includes(searchTerm?.toLowerCase())
    );
  };

  const handleRemoveUserClick = (user: IWorkspaceUser) => {
    setUserToRemove(user);
    setIsRemoveUserModalOpen(true);
  };

  const handleConfirmRemoveUser = () => {
    if (userToRemove && userToRemove._id) {
      const userId =
        typeof userToRemove._id === 'string'
          ? userToRemove._id
          : userToRemove._id._id;
      if (userId) {
        dispatch(removeUserFromWorkspaceRequest({ userId }));
      }
    }
  };

  const handleCancelRemoveUser = () => {
    setIsRemoveUserModalOpen(false);
    setUserToRemove(null);
  };

  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDuplicateEmailOptionChange = (checked: boolean) => {
    if (!checked) {
      setIsDisableDuplicateEmailModalOpen(true);
    } else {
      if (workspace) {
        dispatch(
          updateWorkspaceSettingsRequest({
            duplicateEmailEnabled: checked,
          })
        );
      }
    }
  };

  const handleConfirmDisableDuplicateEmail = (
    mergeOption: 'oldest' | 'newest'
  ) => {
    if (workspace) {
      dispatch(
        updateWorkspaceSettingsRequest({
          duplicateEmailEnabled: false,
          mergeOption,
        })
      );
      setIsDisableDuplicateEmailModalOpen(false);
    }
  };

  const handleCancelDisableDuplicateEmail = () => {
    setIsDisableDuplicateEmailModalOpen(false);
  };

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-foreground mb-1">
          Workspace Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your workspace configuration, members, and preferences
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4" />
            <p className="text-sm text-muted-foreground">
              Loading workspace information...
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-card rounded-xl border border-destructive/50 p-4 shadow-sm">
          <div className="flex items-center text-destructive">
            <Settings className="w-4 h-4 mr-2 shrink-0" />
            <p className="text-sm font-medium">Error loading workspace</p>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
      )}

      {workspace && !loading && !error && (
        <div className="space-y-6">
          {/* Workspace Details Section */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Workspace Details
              </h2>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center text-sm gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Name:</span>
                  <span className="text-foreground font-medium">
                    {workspace.name || 'Unnamed Workspace'}
                  </span>
                  {(currentUser?.role === IUserRole.ADMIN ||
                    currentUser?.role === IUserRole.OWNER) && (
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => setIsUpdateWorkspaceNameModalOpen(true)}
                      className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg"
                      title="Edit workspace name"
                      aria-label="Edit workspace name"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="flex items-center text-sm gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Created:</span>
                  <span className="text-foreground font-medium">
                    {formatDate(workspace.createdAt)}
                  </span>
                </div>
                <div className="flex items-center text-sm gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Last Updated:</span>
                  <span className="text-foreground font-medium">
                    {formatDate(workspace.updatedAt)}
                  </span>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <Switch
                  checked={workspace.duplicateEmailEnabled || false}
                  onChange={handleDuplicateEmailOptionChange}
                  disabled={
                    updateSettingsLoading ||
                    (currentUser?.role !== IUserRole.ADMIN &&
                      currentUser?.role !== IUserRole.OWNER)
                  }
                  label="Allow Duplicate Emails"
                  description="When enabled, contacts with the same email address can be added to this workspace"
                />
              </div>
            </div>
          </div>

          {/* Team Members Section */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-info-background rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-info" />
                </div>
                <h2 className="text-base font-semibold text-foreground">
                  Team Members
                </h2>
                {workspace.users && Array.isArray(workspace.users) && (
                  <span className="px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                    {workspace.users.length}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                  aria-label="Search team members"
                />
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto rounded-xl border border-border">
              {getFilteredUsers().length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {searchTerm ? 'No users found' : 'No team members yet'}
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        Role
                      </th>
                      {(currentUser?.role === IUserRole.ADMIN ||
                        currentUser?.role === IUserRole.OWNER) && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {getFilteredUsers().map((user, index) => (
                      <tr
                        key={typeof user._id === 'string' ? user._id : index}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 h-9 w-9">
                              {user.profile_picture ? (
                                <img
                                  className="h-9 w-9 rounded-full object-cover ring-1 ring-border"
                                  src={user.profile_picture}
                                  alt={`${user.first_name} ${user.last_name}`}
                                />
                              ) : (
                                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center ring-1 ring-border">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {user?.first_name?.charAt(0)}
                                    {user?.last_name?.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-sm font-medium text-foreground">
                              {user.first_name} {user.last_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                          {user.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}
                          >
                            {getRoleIcon(user.role)}
                            {getRoleDisplayName(user.role)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            {currentUser?.role !== IUserRole.MEMBER &&
                              workspace.owner !== user._id &&
                              user._id !== currentUser?._id && (
                                <Button
                                  variant="ghost"
                                  size="small"
                                  onClick={() => handleRemoveUserClick(user)}
                                  className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive-background rounded-lg"
                                  title="Remove user"
                                  aria-label="Remove user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      <Modal
        isOpen={isRemoveUserModalOpen}
        onClose={handleCancelRemoveUser}
        title="Remove Team Member"
        size="small"
      >
        {userToRemove && (
          <div className="space-y-4">
            {/* Warning Icon and Message */}
            <div className="flex items-start gap-3 p-4 bg-destructive-background border border-destructive/50 rounded-xl">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">
                  Are you sure you want to remove this team member?
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. The user will lose access to
                  this workspace immediately.
                </p>
              </div>
            </div>

            {/* User Details */}
            <div className="bg-muted/30 rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-10 w-10">
                  {userToRemove.profile_picture ? (
                    <img
                      className="h-10 w-10 rounded-full object-cover ring-1 ring-border"
                      src={userToRemove.profile_picture}
                      alt={`${userToRemove.first_name} ${userToRemove.last_name}`}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center ring-1 ring-border">
                      <span className="text-sm font-medium text-muted-foreground">
                        {userToRemove?.first_name?.charAt(0)}
                        {userToRemove?.last_name?.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {userToRemove.first_name} {userToRemove.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {userToRemove.email}
                  </div>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mt-1.5 ${getRoleBadgeColor(userToRemove.role)}`}
                  >
                    {getRoleIcon(userToRemove.role)}
                    {getRoleDisplayName(userToRemove.role)}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={handleCancelRemoveUser}
                disabled={removeUserLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmRemoveUser}
                disabled={removeUserLoading}
                className="flex items-center gap-2"
              >
                {removeUserLoading && (
                  <div className="w-4 h-4 border-2 border-destructive-foreground border-t-transparent rounded-full animate-spin shrink-0" />
                )}
                {removeUserLoading ? 'Removing...' : 'Remove Member'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <DisableDuplicateEmailModal
        isOpen={isDisableDuplicateEmailModalOpen}
        onClose={handleCancelDisableDuplicateEmail}
        onConfirm={handleConfirmDisableDuplicateEmail}
        isLoading={updateSettingsLoading}
      />

      <UpdateWorkspaceNameModal
        isOpen={isUpdateWorkspaceNameModalOpen}
        onClose={() => setIsUpdateWorkspaceNameModalOpen(false)}
        currentName={workspace?.name || ''}
      />

      {/* No Data State */}
      {!loading && !error && !workspace && (
        <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-1">
              No Workspace Data
            </h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Unable to load workspace information. Please try refreshing the
              page.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSettings;
